<template>
  <div class="box-shadow h-16 w-full bg-white px-20">
    <div class="flex h-full items-center justify-between">
      <div class="flex h-full items-center">
        <RouterLink
          :to="{ name: logoRouteDefinition.name }"
          @click="setActiveNavigation(logoRouteDefinition.path)"
        >
          <img src="/img/logo.png" alt="logo" class="h-10 cursor-pointer" />
        </RouterLink>
        <div class="ms-20 flex h-full gap-10 font-normal text-iu-anthracite">
          <RouterLink
            v-for="(navigation, index) in navigationStates"
            :to="{ name: navigation.routeName }"
            :id="`navigation-${index}`"
            :key="index"
            @click="setActiveNavigation(navigation.sourcePath)"
            class="flex cursor-pointer items-center"
            :class="{
              'border-b-2 border-iu-anthracite pt-[2px] font-bold text-iu-black': navigation.active
            }"
          >
            {{ navigation.label }}
          </RouterLink>
        </div>
      </div>
      <div
        class="z-10 flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-iu-anthracite"
      >
        <UserIconComponent
          class="scale-75"
          @click="showUserSettings = !showUserSettings"
        ></UserIconComponent>
        <!-- An element that drops down bellow the UserIconComponent if the showUserSettings state is false -->
        <div
          v-if="showUserSettings"
          class="box-shadow absolute right-20 top-14 w-40 rounded-lg border border-iu-grey-light bg-white px-3 py-2 shadow-lg"
        >
          <div class="flex items-center">
            <SpinnerSmallComponent v-if="signingOut"></SpinnerSmallComponent>
            <div
              class="cursor-pointer select-none text-iu-anthracite"
              :class="{ 'text-iu-grey': signingOut }"
              @click="signOut"
            >
              Logout
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { type Ref, ref, onBeforeMount, onMounted, watch } from 'vue';
import { RouterLink, useRouter } from 'vue-router';
import { useAuth } from '@okta/okta-vue';
import { type Navigation } from '@/models';
import { UserIconComponent, SpinnerSmallComponent } from '@/components';

const props = defineProps<{
  navigations: Navigation[];
  pathLevel: number;
  logoRouteDefinition: { path: string; name: string };
}>();

const auth = useAuth();
const router = useRouter();
const navigationStates: Ref<Navigation[]> = ref([]);
const showUserSettings = ref(false);
const signingOut = ref(false);

onBeforeMount(() => {
  navigationStates.value = [...props.navigations];
});

onMounted(async () => {
  await router.isReady();
  setActiveNavigation(router.currentRoute.value.fullPath.split('/')[props.pathLevel]);
});

watch(
  () => router.currentRoute.value.fullPath,
  (newPath) => {
    const pathSegment = newPath.split('/')[props.pathLevel];
    setActiveNavigation(pathSegment);
  }
);

function setActiveNavigation(value: string) {
  navigationStates.value.forEach((navigation) => {
    if (value === navigation.sourcePath) navigation.active = true;
    else navigation.active = false;
  });
}

async function signOut() {
  if (signingOut.value) return;
  signingOut.value = true;
  await auth.signOut();
}
</script>
